/**
 * KnowledgeStats Component
 * Displays knowledge base statistics and metrics
 */

import React from 'react';
import { FiFileText, FiDatabase, FiActivity, FiClock } from 'react-icons/fi';

interface KnowledgeStatsData {
  totalDocuments?: number;
  totalChunks?: number;
  totalTokens?: number;
  lastIndexed?: Date;
  averageChunkSize?: number;
  processingStatus?: 'idle' | 'processing' | 'error';
}

interface KnowledgeStatsProps {
  stats: KnowledgeStatsData | null;
  documentCount: number;
  className?: string;
}

export const KnowledgeStats: React.FC<KnowledgeStatsProps> = ({
  stats,
  documentCount,
  className = ''
}) => {
  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (date: Date): string => {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
      {/* Total Documents */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">Documents</p>
            <p className="text-2xl font-bold text-white">
              {documentCount}
            </p>
          </div>
          <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <FiFileText className="text-blue-400" size={24} />
          </div>
        </div>
      </div>

      {/* Total Chunks */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">Chunks</p>
            <p className="text-2xl font-bold text-white">
              {stats?.totalChunks ? formatNumber(stats.totalChunks) : '0'}
            </p>
            {stats?.averageChunkSize && (
              <p className="text-xs text-gray-500 mt-1">
                ~{stats.averageChunkSize} tokens avg
              </p>
            )}
          </div>
          <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <FiDatabase className="text-purple-400" size={24} />
          </div>
        </div>
      </div>

      {/* Total Tokens */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">Total Tokens</p>
            <p className="text-2xl font-bold text-white">
              {stats?.totalTokens ? formatNumber(stats.totalTokens) : '0'}
            </p>
          </div>
          <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
            <FiActivity className="text-green-400" size={24} />
          </div>
        </div>
      </div>

      {/* Last Indexed */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">Last Indexed</p>
            <p className="text-2xl font-bold text-white">
              {stats?.lastIndexed ? formatDate(stats.lastIndexed) : 'Never'}
            </p>
            {stats?.processingStatus && (
              <div className="mt-2">
                <span className={`
                  inline-block px-2 py-1 text-xs rounded
                  ${stats.processingStatus === 'idle' ? 'bg-gray-500/20 text-gray-400' : ''}
                  ${stats.processingStatus === 'processing' ? 'bg-yellow-500/20 text-yellow-400' : ''}
                  ${stats.processingStatus === 'error' ? 'bg-red-500/20 text-red-400' : ''}
                `}>
                  {stats.processingStatus}
                </span>
              </div>
            )}
          </div>
          <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <FiClock className="text-orange-400" size={24} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeStats;
